import React from "react";
import type { Frame } from "./frame";
import type { Theme } from "./theme";

/**
 * Anivaze v4 — BRAND KIT overlays (PRODUCT-CHAT-EDIT §5).
 *
 * Deterministic overlay layers drawn ON TOP of every frame, OUTSIDE the camera
 * (so they never scroll): a header/footer strip, a corner logo or text
 * watermark, and a brand accent colour. $0 render cost. Preview === export
 * because the Stage renders this in both paths. A per-user brand kit is just this
 * plain object.
 */
export interface BrandKit {
  headerText?: string;
  footerText?: string;
  watermarkText?: string;
  /** data: URI of the user's uploaded logo (png/svg), composited in a corner. */
  logoHref?: string;
  logoPosition?: "tl" | "tr" | "bl" | "br";
  /** Brand accent (hex). Recolours the header + watermark; falls back to theme. */
  accent?: string;
  /** Logo / watermark opacity, 0..1. */
  opacity?: number;
}

export const EMPTY_BRAND: BrandKit = {};

export function hasBrand(b?: BrandKit | null): boolean {
  return !!b && !!(b.headerText || b.footerText || b.watermarkText || b.logoHref);
}

export function BrandOverlay({ brand, frame, theme, unit: u }: { brand?: BrandKit | null; frame: Frame; theme: Theme; unit: number }) {
  if (!hasBrand(brand)) return null;
  const b = brand!;
  const accent = b.accent || theme.palette.primary;
  const op = b.opacity ?? 0.9;
  const els: React.ReactNode[] = [];

  if (b.headerText) {
    els.push(
      <g key="hdr">
        <text x={frame.w / 2} y={u * 3.1} textAnchor="middle" fontFamily={theme.title.family}
          fontWeight={800} fontSize={u * 2.1} letterSpacing={u * 0.06} fill={accent}>{b.headerText}</text>
        <rect x={frame.w / 2 - u * 4} y={u * 3.9} width={u * 8} height={u * 0.32} rx={u * 0.16} fill={accent} opacity={0.85} />
      </g>,
    );
  }
  if (b.footerText) {
    els.push(
      <text key="ftr" x={frame.w / 2} y={frame.h - u * 1.9} textAnchor="middle" fontFamily={theme.body.family}
        fontWeight={600} fontSize={u * 1.7} fill={theme.palette.sub} opacity={0.85}>{b.footerText}</text>,
    );
  }
  if (b.logoHref) {
    const size = u * 6.5;
    const m = u * 2.6;
    const pos = b.logoPosition ?? "br";
    const x = pos.includes("l") ? m : frame.w - m - size;
    const topBias = b.headerText ? u * 4.5 : 0;
    const botBias = b.footerText ? u * 3 : 0;
    const y = pos.includes("t") ? m + topBias : frame.h - m - size - botBias;
    els.push(
      <image key="logo" href={b.logoHref} x={x} y={y} width={size} height={size} opacity={op} preserveAspectRatio="xMidYMid meet" />,
    );
  }
  if (b.watermarkText) {
    // Top-right by default, so it doesn't fight a bottom-right logo.
    els.push(
      <text key="wm" x={frame.w - u * 2.6} y={u * 3.2} textAnchor="end" fontFamily={theme.body.family}
        fontWeight={700} fontSize={u * 1.6} fill={accent} opacity={b.opacity ?? 0.6}>{b.watermarkText}</text>,
    );
  }
  return <g>{els}</g>;
}
